// Adhoc session API — calls /adhoc/* backend routes (/kiosk/* legacy alias).

import { apiFetch } from './apiFetch';

export interface AdhocBoothConfig {
  adhoc_id: string;
  name: string;
  price_cents: number;
  compare_at_cents: number;
  currency: string;
  price_display: string;
  compare_at_display: string;
  discount_label: string;
  disclaimer_version?: string;
}

interface AdhocSessionRaw {
  id: string;
  adhoc_id?: string;
  kiosk_id?: string;
  status: 'pending_payment' | 'paid' | 'scanned' | 'report_sent';
  email: string | null;
  guest_name: string | null;
  sex: string | null;
  date_of_birth: string | null;
  age: number | null;
  height_cm: number | null;
  weight_kg: number | null;
  smoking_status: string | null;
  scan_result_id: string | null;
  expires_at: string;
  is_expired: boolean;
  disclaimer_accepted_at?: string | null;
  disclaimer_version?: string | null;
}

export interface AdhocSession {
  id: string;
  adhoc_id: string;
  status: AdhocSessionRaw['status'];
  email: string | null;
  guest_name: string | null;
  sex: string | null;
  date_of_birth: string | null;
  age: number | null;
  height_cm: number | null;
  weight_kg: number | null;
  smoking_status: string | null;
  scan_result_id: string | null;
  expires_at: string;
  is_expired: boolean;
  disclaimer_accepted_at: string | null;
  disclaimer_version: string | null;
}

export interface AdhocProfileUpdate {
  guest_name?: string;
  email?: string;
  sex?: string;
  date_of_birth?: string;
  age?: number;
  height_cm?: number;
  weight_kg?: number;
  smoking_status?: string;
}

function normalizeSession(raw: AdhocSessionRaw): AdhocSession {
  return {
    id: raw.id,
    adhoc_id: raw.adhoc_id ?? raw.kiosk_id ?? '',
    status: raw.status,
    email: raw.email,
    guest_name: raw.guest_name,
    sex: raw.sex,
    date_of_birth: raw.date_of_birth,
    age: raw.age,
    height_cm: raw.height_cm,
    weight_kg: raw.weight_kg,
    smoking_status: raw.smoking_status,
    scan_result_id: raw.scan_result_id,
    expires_at: raw.expires_at,
    is_expired: raw.is_expired,
    disclaimer_accepted_at: raw.disclaimer_accepted_at ?? null,
    disclaimer_version: raw.disclaimer_version ?? null,
  };
}

async function req<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await apiFetch(path, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

  if (!res.ok) {
    let detail = 'Request failed';
    try {
      const err = await res.json();
      detail = err.detail ?? detail;
    } catch {
      detail = `HTTP ${res.status}`;
    }
    throw { status: res.status, detail };
  }

  try {
    return await res.json();
  } catch {
    throw {
      status: 200,
      detail: 'Invalid response from server. Check ngrok-skip-browser-warning header.',
    };
  }
}

export const getAdhocConfig = (adhoc_id: string): Promise<AdhocBoothConfig> =>
  req(`/adhoc/config/${encodeURIComponent(adhoc_id)}`);

export const createAdhocSession = async (
  adhoc_id: string,
  pack_code?: string,
  owner_token?: string,
): Promise<AdhocSession> => {
  const raw = await req<AdhocSessionRaw>('/adhoc/session', {
    method: 'POST',
    body: JSON.stringify({
      adhoc_id,
      pack_code: pack_code ?? null,
      owner_token: owner_token ?? null,
    }),
  });
  return normalizeSession(raw);
};

export interface AdhocPackOption {
  preset: string;
  scan_count: number;
  price_cents: number;
  price_display: string;
  label: string;
  subtitle: string;
  custom_min?: number;
  custom_max?: number;
}

export interface AdhocPackInfo {
  share_code: string;
  adhoc_id: string;
  scans_purchased: number;
  scans_remaining: number;
  total_scans_remaining?: number | null;
  share_url?: string | null;
  owner_token?: string | null;
  label: string;
  subtitle: string;
  pool_type?: string | null;
}

export const getAdhocPackOptions = (): Promise<AdhocPackOption[]> =>
  req('/adhoc/packs');

export const getAdhocPack = (
  shareCode: string,
  ownerToken?: string,
): Promise<AdhocPackInfo> => {
  const qs = ownerToken
    ? `?owner_token=${encodeURIComponent(ownerToken)}`
    : '';
  return req(`/adhoc/pack/${encodeURIComponent(shareCode)}${qs}`);
};

export const getAdhocPackByCheckout = (
  checkoutId: string,
  provider: 'stripe' | 'hitpay' = 'stripe',
): Promise<AdhocPackInfo> => {
  const qs = `?provider=${encodeURIComponent(provider)}`;
  return req(`/adhoc/pack/by-checkout/${encodeURIComponent(checkoutId)}${qs}`);
};

export const createAdhocPackCheckout = (body: {
  adhoc_id: string;
  preset: '5' | '10' | 'custom';
  custom_scans?: number;
  provider?: 'stripe' | 'hitpay';
  extend_pack_code?: string;
  extend_owner_token?: string;
}): Promise<{ checkout_url: string; checkout_id: string; provider: 'stripe' | 'hitpay' }> =>
  req('/adhoc/pack/checkout', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const createAdhocCreditsCheckout = (body: {
  adhoc_id: string;
  scan_count: number;
  provider?: 'stripe' | 'hitpay';
}): Promise<{ checkout_url: string; checkout_id: string; provider: 'stripe' | 'hitpay' }> =>
  req('/adhoc/credits/checkout', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const getAdhocCreditsByCheckout = (
  checkoutId: string,
  provider: 'stripe' | 'hitpay' = 'stripe',
): Promise<AdhocPackInfo> => {
  const qs = `?provider=${encodeURIComponent(provider)}`;
  return req(`/adhoc/credits/by-checkout/${encodeURIComponent(checkoutId)}${qs}`);
};

export const getMyAdhocCredits = (adhocId: string): Promise<AdhocPackInfo> =>
  req(`/adhoc/credits/mine?adhoc_id=${encodeURIComponent(adhocId)}`);

export const accessAdhocPackByToken = (token: string): Promise<AdhocPackInfo> =>
  req(`/adhoc/pack/access?token=${encodeURIComponent(token)}`);

export const getAdhocSession = async (id: string): Promise<AdhocSession> => {
  const raw = await req<AdhocSessionRaw>(`/adhoc/session/${id}`);
  return normalizeSession(raw);
};

export const updateAdhocProfile = async (
  id: string,
  data: AdhocProfileUpdate,
): Promise<AdhocSession> => {
  const raw = await req<AdhocSessionRaw>(`/adhoc/session/${id}/profile`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return normalizeSession(raw);
};

export const acceptAdhocDisclaimer = (
  id: string,
  version: string,
): Promise<AdhocSession> =>
  req<AdhocSessionRaw>(`/adhoc/session/${id}/disclaimer`, {
    method: 'POST',
    body: JSON.stringify({ version }),
  }).then(normalizeSession);

export const createAdhocCheckout = (
  id: string,
  body?: { provider?: 'stripe' | 'hitpay' },
): Promise<{ checkout_url: string; checkout_id: string; provider: 'stripe' | 'hitpay' }> =>
  req(`/adhoc/session/${id}/checkout`, {
    method: 'POST',
    body: JSON.stringify(body ?? { provider: 'stripe' }),
  });

export const saveAdhocScan = (
  id: string,
  vitals: Record<string, unknown>,
  meta?: {
    scan_platform?: string;
    measurement_duration_sec?: number;
    vitals_enabled?: Record<string, boolean>;
  },
): Promise<{ scan_result_id: string; status: string }> =>
  req(`/adhoc/session/${id}/scan`, {
    method: 'POST',
    body: JSON.stringify({
      vitals,
      scan_platform: meta?.scan_platform ?? 'adhoc',
      measurement_duration_sec: meta?.measurement_duration_sec ?? null,
      vitals_enabled: meta?.vitals_enabled ?? null,
    }),
  });

export const sendAdhocReport = (
  id: string,
  email?: string,
): Promise<{ message: string }> =>
  req(`/adhoc/session/${id}/email`, {
    method: 'POST',
    body: JSON.stringify({ email: email ?? null }),
  });

export const adhocPdfUrl = (id: string): string => {
  const base = process.env.BACKEND_URL || '';
  return `${base}/adhoc/session/${id}/pdf`;
};
